import type { ColumnSeparator, MarkerSetupType } from '@core/core/types'
import { columnSeparators } from '@core/core/types'
import { useEffect } from 'react'

import { getSeparatorRegex } from './helper'

export function useSettingsPredictor(
  lines: string[] | undefined,
  update: (diff: Partial<MarkerSetupType>) => void,
) {
  useEffect(() => {
    if (!lines) return

    const trimmedLines = trimLines(lines)
    if (trimmedLines.length === 0) return

    const separator = getProbableSeparator(trimmedLines)
    const missedLinesCount = getProbableHeaderLines(trimmedLines, separator)

    update({ separator, missedLinesCount })
  }, [lines])
}

type SeparatorMatchCount = {
  separator: ColumnSeparator
  count: number
}

function trimLines(lines: string[]): string[] {
  // Индекс последней непустой строки
  const lastNonEmptyLineIndex = lines.reduce<number>(
    (prevIndex, line, curIndex) => (line.trim().length > 0 ? curIndex : prevIndex),
    0,
  )

  return lines.slice(0, lastNonEmptyLineIndex + 1)
}

function getProbableSeparator(lines: string[]): ColumnSeparator {
  const separatorWithMatchCount = columnSeparators.reduce<SeparatorMatchCount>(
    (prev, separator) => {
      const separatorExpr = new RegExp(getSeparatorRegex(separator), 'g')
      // Берем из последней строки, чтобы исключить влияние заголовка,
      // тк в заголовке может быть много левых разделителей
      const count = lines.at(-1)?.match(separatorExpr)?.length ?? 0

      return prev.count < count ? { separator, count } : prev
    },
    { separator: 'space', count: 0 },
  )

  return separatorWithMatchCount.separator
}

function getProbableHeaderLines(lines: string[], separator: ColumnSeparator): number {
  const separatorExpr = new RegExp(getSeparatorRegex(separator), 'g')

  let index = lines.length - 1
  const lastLine = lines[index]
  const lastColCount = lastLine.match(separatorExpr)?.length
  const lastNumColCount = getNumberColumnCount(lastLine, separator)

  while (index > 0) {
    const prevIndex = index
    index -= 1

    const line = lines[index]
    const colCount = line.match(separatorExpr)?.length
    if (colCount !== lastColCount) return prevIndex

    const numColCount = getNumberColumnCount(line, separator)
    // Некоторые скважины могут быть как число
    if (Math.abs(numColCount - lastNumColCount) > 1) return prevIndex
  }

  return 0
}

function getNumberColumnCount(line: string, separator: ColumnSeparator): number {
  return line
    .split(getSeparatorRegex(separator))
    .filter(value => Number.isFinite(Number.parseFloat(value))).length
}
